import type { SelectChangeEvent } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import type { FC } from 'react';
import { useCallback, useRef } from 'react';
import { useProjects } from '../api/getProjects';
import type { Option } from 'types/shared';
import IconWT from 'assets/icon-welltech.svg?react';
import IconFC from 'assets/icon-fc.svg?react';
import IconYg from 'assets/icon-yg.svg?react';
import IconMB from 'assets/icon-mb.svg?react';
import IconOM from 'assets/icon-om.svg?react';
import IconWF from 'assets/icon-wf.svg?react';
import { ALL_PROJECTS, selectedProjectStore } from '../stores/selectedProjectStore';
import { observer } from 'mobx-react-lite';

const FilterProjects: FC = observer(() => {
  const iconsRef = useRef<Record<string, any>>({
    WT: <IconWT width={22} height={20} />,
    FC: <IconFC width={22} height={20} />,
    YG: <IconYg width={22} height={20} />,
    MB: <IconMB width={22} height={20} />,
    OM: <IconOM width={22} height={20} />,
    WF: <IconWF width={22} height={20} />,
    default: <IconWT width={22} height={20} />,
  });
  const { data } = useProjects<Option[]>({
    config: {
      select: (data) => [
        ...data.filter((prj) => prj.id !== 0).map(({ name, id }) => ({ value: `${id}`, label: name })),
        { value: ALL_PROJECTS.id, label: ALL_PROJECTS.name },
      ],
    },
  });

  const handleChange = useCallback(
    (e: SelectChangeEvent) => {
      const searchedProj = data.find((d) => d.value === e.target.value);
      if (searchedProj) {
        selectedProjectStore.changeProject({ id: String(searchedProj.value), name: searchedProj.label });
      }
    },
    [data]
  );

  return (
    <Select
      inputProps={{
        'data-testid': 'filter-projects-select',
      }}
      value={String(selectedProjectStore.selectedProject?.id)}
      onChange={(e: SelectChangeEvent) => {
        handleChange(e);
      }}
      sx={(t) => ({
        '&': {
          minWidth: 68,
          minHeight: 25,
        },
        '& .MuiSelect-outlined.MuiSelect-select': {
          fontSize: t.typography.h3.fontSize,
          minWidth: 68,
          minHeight: 25,
        },
        '& .MuiOutlinedInput-notchedOutline': {
          border: 'none',
        },
      })}
      renderValue={(value) => (
        <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', flexWrap: 'nowrap' }}>
          <Box sx={{ mr: 1 }}>
            {iconsRef.current[data.find((option) => option.value === value)?.label ?? 'default'] ?? iconsRef.current.default}
          </Box>
          <Typography
            sx={(t) => ({
              fontSize: t.typography.h3.fontSize,
              lineHeight: '23px',
              height: '24px',
            })}
          >
            {data.find((option) => option.value === value)?.label}
          </Typography>
        </Box>
      )}
      MenuProps={{
        sx: {
          '& .MuiPaper-root': {
            marginTop: 2,
            minWidth: '200px !important',
          },
        },
      }}
    >
      {data.map((option) => (
        <MenuItem
          key={`project:${option.value}:${option.label}`}
          value={option.value}
          disabled={option.value === '0'}
          data-testid={`filter-projects-menuitem-${option.label}`}
        >
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

export default FilterProjects;
